import React, { FC, useState, useEffect, useMemo, Fragment } from 'react';
import clsx, { ClassValue } from 'clsx';
import { useLazyQuery } from '@apollo/client';
import { fullName, photoPlaceholder } from 'utils/helpers';
import { IMenuTypes, INotifications } from 'utils/models';
import query from 'graphql/query';
import useAuth from 'hooks/useAuth';
import { Link } from 'react-router-dom';

// components
import MenuAction from 'components/MenuAction';
import PopOver from 'components/PopOver';
import Notifications from 'components/notifications/Notifications';
import { SvgSideSettings, SvgEnvelope } from 'components/SvgIcons';

interface IHeader {
  className?: ClassValue;
  showNotification?: boolean;
  menu: IMenuTypes[];
}

const Header: FC<IHeader> = ({ className, showNotification = false, menu }) => {
  const { user, isAdmin } = useAuth();
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState<boolean>(false);

  const [doctorNotifications, { data }] = useLazyQuery<{
    doctorNotifications: {
      code: string;
      success: boolean;
      message: string;
      notifications: INotifications[];
    };
  }>(query.doctorNotifications, {
    fetchPolicy: 'network-only'
  });
  const notifications = data?.doctorNotifications?.notifications;
  const notificationsWithDefault = useMemo(() => notifications || [], [notifications]);

  useEffect(() => {
    if (notificationsWithDefault.length > 0) {
      const unReadNotification = notificationsWithDefault.find(({ is_read }) => !is_read);
      setHasUnreadNotifications((unReadNotification && 'is_read' in unReadNotification) || false);
    }
  }, [notificationsWithDefault]);

  useEffect(() => {
    doctorNotifications();
  }, [doctorNotifications]);

  return (
    <div
      className={clsx(className, 'relative w-full', isAdmin ? 'bg-red-300' : 'bg-hazel-white-600 ')}
    >
      <div className="p-3 mx-auto">
        {/* <div className="flex justify-between items-center">
          <div className="flex justify-start flex-col items-left text-left">
            <Link to="/dashboard">
              <div className="bg-hazel-logo bg-center bg-no-repeat bg-contain h-14 md:h-8 w-22 mb-2" />
              <div className="text-hazel-light-gray-200 font-semibold text-xxs uppercase -mt-1">
                Doctor Dashboard
              </div>
            </Link>
          </div> */}

        <div className="flex justify-between items-center">
          <Link to="/dashboard">
            <div className="bg-hazel-logo bg-center bg-no-repeat bg-contain h-8 md:h-10 w-16" />
          </Link>
          <div className="flex-grow border-l border-slate-300 mx-8">
            <div className="font-Sohne-Light tracking-wide text-xs uppercase py-2 px-5">
              Doctor Dashboard
            </div>
          </div>

          {isAdmin && (
            <div className="flex justify-center p-2 uppercase text-xl font-bold">
              You are logged-in as admin
            </div>
          )}

          <div className="flex justify-end items-center">
            <div className="hidden md:inline-block">
              <div className="flex items-center space-x-5">
                <div className="flex items-center space-x-5 mr-10">
                  <div
                    className={clsx(
                      'bg-no-repeat bg-cover bg-center',
                      'rounded-full h-12 w-12 border'
                    )}
                    style={{
                      backgroundImage: `url(${
                        user?.profile?.profile_pic || photoPlaceholder['doctor']
                      })`
                    }}
                  />

                  {user?.profile?.first_name && user?.profile?.last_name && (
                    <div>
                      <div className="font-semibold text-hazel-nude-100 text-sm">
                        {fullName(user)}
                      </div>
                      <div className="text-hazel-light-gray-200 font-base uppercase text-xs">
                        {user?.doctor_type?.description || '-'}
                      </div>
                    </div>
                  )}
                </div>
                {showNotification && (
                  <div className="relative mb-1">
                    <PopOver
                      placement="bottom-end"
                      offSetX={40}
                      offSetY={10}
                      classWidth="sm:w-6/12 2xl:w-4/12"
                      btnToggle={
                        <Fragment>
                          <SvgEnvelope className="w-6 h-6 cursor-pointer text-hazel-light-gray-200" />
                          <div
                            className={clsx(
                              'w-3 h-3 ',
                              'absolute -top-1 -right-1',
                              'w-3 h-3 ',
                              'absolute -top-1 -right-1',
                              'rounded-full',
                              hasUnreadNotifications ? 'bg-hazel-red-100' : 'bg-transparent'
                            )}
                          />
                        </Fragment>
                      }
                    >
                      <h2 className="text-xl font-extrabold mb-2">Messages</h2>
                      <Notifications setHasUnreadNotifications={setHasUnreadNotifications} />
                    </PopOver>
                  </div>
                )}
              </div>
            </div>

            <div className="hidden md:block">
              <MenuAction
                menuButton={<SvgSideSettings className="w-6 h-6 text-hazel-nude-100" />}
                classNameMain="ml-5"
                menu={menu}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
